import { useMemo } from "react"
import styled from "styled-components"

import { Banner, Skeleton, spacing, Text } from "@minuthq/meatball-ui-react"

import { GuestSource } from "src/components/Guests/GuestDetails/GuestSource"
import { extendGuest } from "src/components/Homes/Guests/guestsUtil"
import { TGuest } from "src/data/guests/types/guestTypes"
import { useFetchIntegrations } from "src/data/integrations/queries/integrationQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHome } from "src/data/organizations/queries/homeQueries"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { InternalLink } from "src/ui/Link/InternalLink"
import { formatDate } from "src/utils/l10n"

export function GuestConversationDetails({ guest }: { guest: TGuest }) {
  const { t, langKeys } = useTranslate()

  const { orgId } = useOrganization()

  const user = useGetUser()

  const fetchHome = useFetchHome({
    orgId,
    homeId: guest.home_id,
  })

  const fetchIntegrations = useFetchIntegrations({
    orgId,
    filters: { includeExternalIntegrations: true },
  })

  const extendedGuest = useMemo(() => {
    const integrations = fetchIntegrations.data?.integrations ?? []

    return extendGuest(guest, integrations)
  }, [guest, fetchIntegrations.data?.integrations])

  if (fetchHome.isError) {
    return <Banner type="error">{t(langKeys.failed_contact_support)}</Banner>
  }

  if (fetchHome.isLoading) {
    return <Skeleton width="50%" />
  }

  const home = fetchHome.data

  return (
    <Wrapper>
      <InternalLink to={Routes.Home.location(fetchHome.data.home_id)}>
        <Text variant="heading3">{fetchHome.data.name}</Text>
      </InternalLink>

      <DetailsGrid>
        <Detail label={t(langKeys.guest_check_in)}>
          {formatDate({
            date: guest.checkin_time ?? "",
            clockType: user.clock_type,
            timezone: home.timezone,
          })}
        </Detail>

        <Detail label={t(langKeys.guest_check_out)}>
          {formatDate({
            date: guest.checkout_time ?? "",
            clockType: user.clock_type,
            timezone: home.timezone,
          })}
        </Detail>

        <Detail label={t(langKeys.phone)}>{guest.phone_number}</Detail>

        <Detail label={t(langKeys.email)}>{guest.email}</Detail>

        <Detail label={t(langKeys.lock_access_code)}>
          {guest.access_code}
        </Detail>

        <Detail label={t(langKeys.guest_source)}>
          <GuestSource extendedGuest={extendedGuest} />
        </Detail>
      </DetailsGrid>
    </Wrapper>
  )
}

function Detail({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <Text variant="bodyS" color="secondary">
        {label}
      </Text>
      {children}
    </div>
  )
}

const Wrapper = styled.div`
  display: grid;
  gap: ${spacing.M};
`

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${spacing.L};
`
